import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { api, Client, SentimentCap8, SentimentMarket, MeetingSentiment, QueryResult, QueryHistory, InsightCitation, PulseClient, Commitment } from '../lib/api';
import { RenderWithCitations } from '../components/CitationChip';
import { AppOutletContext } from '../App';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  citations?: InsightCitation[];
  relatedQuestions?: string[];
  sourcesUsed?: string[];
  streaming?: boolean;
  timestamp: Date;
}

// Sentiment scores for trend calculation
const SENTIMENT_SCORES: Record<SentimentCap8, number> = {
  negative: 1,
  neutral: 2,
  positive: 3,
  very_positive: 4,
};

const SENTIMENT_CAP8_COLORS: Record<SentimentCap8, string> = {
  very_positive: '#22c55e',
  positive: '#86efac',
  neutral: '#94a3b8',
  negative: '#ef4444',
};

const SENTIMENT_MARKET_COLORS: Record<SentimentMarket, string> = {
  euphoric: '#22c55e',
  bull: '#86efac',
  bear: '#f97316',
  panic: '#ef4444',
};

interface ClientMoodChip {
  client_id: string;
  client_name: string;
  meetings: MeetingSentiment[];
  trend: 'degrading' | 'stable' | 'improving';
  trendScore: number;
}

const EXAMPLE_QUESTIONS = [
  'What has Middleton said about fees over the last 3 meetings?',
  'Which clients mentioned compliance concerns this quarter?',
  'What are the top feature requests from live clients?',
  'How has sentiment changed for prospects in the pipeline?',
];

// Branch display labels
const BRANCH_LABELS: Record<string, string> = {
  engineering: 'Engineering',
  data_science: 'Data Science',
  finance_economics: 'Finance & Economics',
};

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export function Dashboard() {
  const navigate = useNavigate();
  // Use outlet context for current user (falls back gracefully if not yet loaded)
  const outletCtx = useOutletContext<AppOutletContext>();
  const contextUser = outletCtx?.currentUser ?? null;

  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState<Client[]>([]);
  const [moodChips, setMoodChips] = useState<ClientMoodChip[]>([]);
  const [stableClients, setStableClients] = useState<ClientMoodChip[]>([]);
  const [showStable, setShowStable] = useState(false);
  const [sentimentType, setSentimentType] = useState<'cap8' | 'market'>('cap8');
  const [attentionCount, setAttentionCount] = useState(0);

  // Attention cards data
  const [overdueCommitment, setOverdueCommitment] = useState<Commitment | null>(null);
  const [degradingClient, setDegradingClient] = useState<PulseClient | null>(null);
  const [triageTotal, setTriageTotal] = useState(0);
  const [triageOldest, setTriageOldest] = useState<{ insight_text: string; client_name?: string; meeting_date: string; branch: string | null } | null>(null);

  // Suggested chips text (computed once on load)
  const [suggestedChips, setSuggestedChips] = useState<string[]>([]);

  // Ask bar state
  const [question, setQuestion] = useState('');
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [askLoading, setAskLoading] = useState(false);
  const [stageLabel, setStageLabel] = useState('');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [recentQueries, setRecentQueries] = useState<QueryHistory[]>([]);
  const [askError, setAskError] = useState('');
  const [exampleQuestion] = useState(() => EXAMPLE_QUESTIONS[Math.floor(Math.random() * EXAMPLE_QUESTIONS.length)]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadDashboardData();
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadDashboardData = async () => {
    try {
      const [clientList, moodData, queryHist, relationships, triageResult] = await Promise.all([
        api.listClients(),
        api.getMoodGrid({ limit: 6 }),
        api.listQueryHistory(8).catch(() => [] as QueryHistory[]),
        api.getPulseRelationships().catch(() => null),
        api.getTriageQueue({ limit: 1 }).catch(() => null),
      ]);

      setClients(clientList);
      setRecentQueries(queryHist.filter(q => q.question && q.answer).slice(0, 4));

      // Process mood data into chips with trends
      const allChips: ClientMoodChip[] = moodData.clients.map(client => {
        const meetingsWithSentiment = client.meetings.filter(m => m.sentiment_cap8);
        let trend: 'degrading' | 'stable' | 'improving' = 'stable';
        let trendScore = 0;

        if (meetingsWithSentiment.length >= 2) {
          const recent = meetingsWithSentiment[0];
          const recentScore = SENTIMENT_SCORES[recent.sentiment_cap8!];
          const olderScores = meetingsWithSentiment.slice(1).map(m => SENTIMENT_SCORES[m.sentiment_cap8!]);
          const olderAvg = olderScores.reduce((a, b) => a + b, 0) / olderScores.length;
          const secondScore = SENTIMENT_SCORES[meetingsWithSentiment[1].sentiment_cap8!];

          trendScore = recentScore - olderAvg;

          // "Needs attention" should mean the relationship is actually at risk,
          // not just any dip. A single positive->neutral blip (especially at
          // low confidence) is NOT an alert. We flag degrading only when the
          // latest meeting is genuinely negative, or there's a sustained slide
          // where the two most recent meetings are both neutral-or-worse.
          const recentConfident = recent.sentiment_cap8_confidence !== 'low';
          const isNegativeLatest = recent.sentiment_cap8 === 'negative';
          const sustainedDecline = recentScore <= 2 && secondScore <= 2 && trendScore < 0;

          if (recentConfident && (isNegativeLatest || sustainedDecline)) {
            trend = 'degrading';
          } else if (trendScore > 0.5) {
            trend = 'improving';
          }
          // else stable (covers single positive->neutral dips and low-confidence latest)
        }

        return {
          client_id: client.client_id,
          client_name: client.client_name,
          meetings: client.meetings,
          trend,
          trendScore,
        };
      });

      // Separate attention-needing clients from stable ones
      const attention = allChips.filter(c => c.trend !== 'stable');
      const stable = allChips.filter(c => c.trend === 'stable');

      // Sort: degrading first
      attention.sort((a, b) => a.trendScore - b.trendScore);

      setMoodChips(attention);
      setStableClients(stable);
      setAttentionCount(attention.filter(c => c.trend === 'degrading').length);

      // Attention cards: pull from pulse relationships
      let firstOverdue: Commitment | null = null;
      let firstDegrading: PulseClient | null = null;

      if (relationships) {
        firstOverdue = relationships.commitment_attention.overdue_cap8[0] ?? null;
        firstDegrading = relationships.clients.find(
          c => c.trend === 'degrading' && !c.acked
        ) ?? null;
      }

      setOverdueCommitment(firstOverdue);
      setDegradingClient(firstDegrading);

      // Triage card
      if (triageResult) {
        setTriageTotal(triageResult.total);
        if (triageResult.insights[0]) {
          const oldest = triageResult.insights[0];
          setTriageOldest({
            insight_text: oldest.insight_text,
            client_name: oldest.client_name,
            meeting_date: oldest.meeting_date,
            branch: oldest.branch,
          });
        }
      }

      // Build suggested question chips from live data
      const chips: string[] = [];
      if (firstDegrading) {
        chips.push(`What is ${firstDegrading.client_name} unhappy about?`);
      }
      if (triageResult && triageResult.total > 0 && triageResult.insights[0]) {
        const branchLabel = triageResult.insights[0].branch
          ? (BRANCH_LABELS[triageResult.insights[0].branch] ?? 'data science')
          : 'data science';
        chips.push(`What are the top ${branchLabel} asks this quarter?`);
      }
      if (firstOverdue) {
        chips.push(`What did we promise ${firstOverdue.client_name}?`);
      }
      chips.push('What changed in the last month?');
      setSuggestedChips(chips.slice(0, 4));
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const submitQuestion = async (userQuestion: string) => {
    if (!userQuestion.trim() || askLoading) return;

    setQuestion('');
    setAskError('');
    setAskLoading(true);
    setStageLabel('Thinking...');

    // Prior turns give the model follow-up context ("those clients", "that feature")
    const conversationHistory = chatHistory
      .slice(-6)
      .map(m => ({ role: m.role, content: m.content }));

    setChatHistory(prev => [...prev, { role: 'user', content: userQuestion, timestamp: new Date() }]);
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

    const filters: { client_id?: string; client_ids?: string[] } = {};
    if (selectedClientIds.length === 1) {
      filters.client_id = selectedClientIds[0];
    } else if (selectedClientIds.length > 1) {
      filters.client_ids = selectedClientIds;
    }
    // If no clients selected (All clients), no filter is passed - queries all

    let receivedTokens = false;
    let finished = false;
    let streamError = '';

    const appendDelta = (text: string) => {
      setChatHistory(prev => {
        const last = prev[prev.length - 1];
        if (last && last.role === 'assistant' && last.streaming) {
          return [...prev.slice(0, -1), { ...last, content: last.content + text }];
        }
        return [...prev, { role: 'assistant', content: text, streaming: true, timestamp: new Date() }];
      });
    };

    const finalize = (result: QueryResult) => {
      finished = true;
      const finalMsg: ChatMessage = {
        role: 'assistant',
        content: result.answer,
        citations: result.citations,
        relatedQuestions: result.related_questions,
        sourcesUsed: result.sources_used,
        timestamp: new Date(),
        // Store auto_filters for display
        ...(result.auto_filters ? { autoFilters: result.auto_filters } : {}),
      };
      setChatHistory(prev => {
        const last = prev[prev.length - 1];
        if (last && last.role === 'assistant' && last.streaming) {
          return [...prev.slice(0, -1), finalMsg];
        }
        return [...prev, finalMsg];
      });
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    };

    try {
      await api.executeQueryStream(filters, userQuestion, conversationHistory, {
        onStage: s => setStageLabel(s.label),
        onDelta: t => {
          receivedTokens = true;
          appendDelta(t);
        },
        onDone: finalize,
        onError: m => { streamError = m; },
      });
      if (streamError && !finished) {
        throw new Error(streamError);
      }
      if (!finished && !receivedTokens) {
        throw new Error('Stream ended unexpectedly');
      }
    } catch (err) {
      // Streaming failed; if no answer text arrived, fall back to the JSON endpoint
      if (!receivedTokens && !finished) {
        try {
          setStageLabel('Retrying...');
          const result = await api.executeQuery('filtered', filters, userQuestion, conversationHistory) as QueryResult;
          finalize(result);
        } catch (fallbackErr) {
          setAskError(fallbackErr instanceof Error ? fallbackErr.message : 'Failed to get answer');
          setChatHistory(prev => prev.slice(0, -1));
        }
      } else if (!finished) {
        setAskError(err instanceof Error ? err.message : 'Failed to get answer');
        // Drop the partial assistant message and the user turn
        setChatHistory(prev => {
          const trimmed = prev[prev.length - 1]?.role === 'assistant' ? prev.slice(0, -1) : prev;
          return trimmed[trimmed.length - 1]?.role === 'user' ? trimmed.slice(0, -1) : trimmed;
        });
      }
    } finally {
      setAskLoading(false);
      setStageLabel('');
    }
  };

  const handleAsk = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitQuestion(question.trim());
  };

  const loadHistoryItem = (item: QueryHistory) => {
    if (!item.question || !item.answer) return;
    let citations: InsightCitation[] | undefined;
    try {
      citations = item.citations ? JSON.parse(item.citations) : undefined;
    } catch {
      citations = undefined;
    }
    setAskError('');
    setChatHistory([
      { role: 'user', content: item.question, timestamp: new Date(item.created_at) },
      { role: 'assistant', content: item.answer, citations, timestamp: new Date(item.created_at) },
    ]);
  };

  const clearChat = () => {
    setChatHistory([]);
    setAskError('');
  };

  const toggleClient = (clientId: string) => {
    setSelectedClientIds(prev =>
      prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]
    );
  };

  const selectAllClients = () => setSelectedClientIds(clients.map(c => c.client_id));
  const clearClients = () => setSelectedClientIds([]);

  const liveClients = clients.filter(c => c.status === 'live_client' || c.status === 'signed_client');
  const prospectClients = clients.filter(c => c.status.startsWith('prospect'));
  const firstName = (contextUser?.name ?? 'there').split(' ')[0];
  const isInvestor = contextUser?.role === 'investor';

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Ambient Owl Background */}
      <div className="dashboard-owl-bg" />

      <div className="dashboard-content">
        {/* HERO Section */}
        <div className="dashboard-hero">
          <h1 className="dashboard-greeting">{getGreeting()}, {firstName}</h1>

          {/* Compact situation summary */}
          <p className="dashboard-summary">
            {attentionCount > 0 ? (
              <span className="summary-alert">{attentionCount} client{attentionCount > 1 ? 's' : ''} need attention</span>
            ) : (
              <span className="summary-ok">All clients stable</span>
            )}
          </p>

          {/* Ask Bar - Expandable Chat Container */}
          <div className={`ask-bar-container ${chatHistory.length > 0 || askLoading || askError ? 'has-messages' : ''}`}>
            {/* Chat Messages (when active) */}
            {(chatHistory.length > 0 || askLoading || askError) && (
              <div className="ask-bar-messages">
                <div className="ask-bar-messages-header">
                  {chatHistory.length > 0 && (
                    <button className="btn btn-ghost btn-small" onClick={clearChat}>Clear</button>
                  )}
                </div>
                <div className="ask-bar-messages-content">
                  {chatHistory.map((msg, i) => (
                    <div key={i} className={`chat-message chat-message-${msg.role}`}>
                      <div className="chat-message-content">
                        {msg.role === 'assistant' ? (
                          <div className="chat-answer">
                            {/* Auto-filters line: show when available and message is finalized */}
                            {!msg.streaming && (msg as ChatMessage & { autoFilters?: Record<string, unknown> }).autoFilters && (() => {
                              const af = (msg as ChatMessage & { autoFilters?: Record<string, unknown> }).autoFilters!;
                              const parts: string[] = [];
                              if (af.client_id || af.client_ids) parts.push('client scoped');
                              if (af.branch && typeof af.branch === 'string') parts.push(BRANCH_LABELS[af.branch] ?? af.branch);
                              if (af.date_start || af.date_end) parts.push('date filtered');
                              return parts.length > 0 ? (
                                <div className="chat-sources-line" style={{ marginBottom: '0.5rem', color: 'var(--text-tertiary)', fontSize: '0.75rem' }}>
                                  Filters: {parts.join(', ')}
                                </div>
                              ) : null;
                            })()}
                            <RenderWithCitations content={msg.content} citations={msg.citations} />
                            {msg.streaming && <span className="chat-cursor" aria-hidden="true" />}
                            {!msg.streaming && msg.sourcesUsed && msg.sourcesUsed.length > 0 && (
                              <div className="chat-sources-line">
                                Sources: {msg.sourcesUsed.join(' · ')}
                              </div>
                            )}
                            {!msg.streaming && msg.relatedQuestions && msg.relatedQuestions.length > 0 && (
                              <div className="related-questions">
                                <span className="related-questions-label">Follow up:</span>
                                {msg.relatedQuestions.map((rq, j) => (
                                  <button
                                    key={j}
                                    type="button"
                                    className="related-question-chip"
                                    onClick={() => submitQuestion(rq)}
                                    disabled={askLoading}
                                  >
                                    {rq}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <p>{msg.content}</p>
                        )}
                      </div>
                    </div>
                  ))}
                  {askLoading && (
                    <div className="chat-stage" role="status">
                      <div className="chat-loading">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                      <span className="chat-stage-label">{stageLabel}</span>
                    </div>
                  )}
                  {askError && <div className="chat-error">{askError}</div>}
                  <div ref={chatEndRef} />
                </div>
              </div>
            )}

            {/* Input Bar */}
            <form className="ask-bar-hero" onSubmit={handleAsk}>
              <div className="ask-bar-wrapper">
                <div className="ask-bar-client-select" ref={dropdownRef}>
                  <button
                    type="button"
                    className="ask-bar-client-btn"
                    onClick={() => setShowClientDropdown(!showClientDropdown)}
                  >
                    {selectedClientIds.length === 0
                      ? 'All clients'
                      : `${selectedClientIds.length} client${selectedClientIds.length > 1 ? 's' : ''}`}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>

                  {showClientDropdown && (
                    <div className="ask-bar-dropdown">
                      <div className="ask-bar-dropdown-actions">
                        <button type="button" onClick={selectAllClients}>Select All</button>
                        <button type="button" onClick={clearClients}>Clear</button>
                      </div>
                      {liveClients.length > 0 && (
                        <div className="ask-bar-dropdown-group">
                          <div className="ask-bar-dropdown-label">Live</div>
                          {liveClients.map(c => (
                            <label key={c.client_id} className="ask-bar-dropdown-item">
                              <input
                                type="checkbox"
                                checked={selectedClientIds.includes(c.client_id)}
                                onChange={() => toggleClient(c.client_id)}
                              />
                              <span>{c.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                      {prospectClients.length > 0 && (
                        <div className="ask-bar-dropdown-group">
                          <div className="ask-bar-dropdown-label">Prospect</div>
                          {prospectClients.map(c => (
                            <label key={c.client_id} className="ask-bar-dropdown-item">
                              <input
                                type="checkbox"
                                checked={selectedClientIds.includes(c.client_id)}
                                onChange={() => toggleClient(c.client_id)}
                              />
                              <span>{c.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <input
                  type="text"
                  className="ask-bar-input"
                  placeholder={chatHistory.length > 0 ? "Ask a follow-up question..." : "Ask anything about your client conversations..."}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                />

                <button type="submit" className="btn btn-gradient" disabled={askLoading}>
                  {askLoading ? (
                    <span className="btn-loading-spinner"></span>
                  ) : 'Ask'}
                </button>
              </div>
            </form>
          </div>

          {/* Suggested chips + recent questions shown when no active chat */}
          {!chatHistory.length && !askLoading && !askError && (
            <>
              {/* Suggested question chips */}
              {suggestedChips.length > 0 && (
                <div className="suggested-chips">
                  {suggestedChips.map((chip, i) => (
                    <button
                      key={i}
                      type="button"
                      className="related-question-chip"
                      onClick={() => setQuestion(chip)}
                    >
                      {chip}
                    </button>
                  ))}
                </div>
              )}
              <p className="ask-bar-hint">e.g. "{exampleQuestion}"</p>
              {recentQueries.length > 0 && (
                <div className="recent-questions">
                  <span className="recent-questions-label">Recent:</span>
                  {recentQueries.map(q => (
                    <button
                      key={q.query_id}
                      type="button"
                      className="recent-question-chip"
                      onClick={() => loadHistoryItem(q)}
                      title={q.question || ''}
                    >
                      {(q.question || '').length > 64 ? `${q.question!.slice(0, 64)}…` : q.question}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Client Situation - Attention-first mood strip */}
        <div className="mood-strip-section">
          <div className="mood-strip-header">
            <span className="section-title">Client Situation</span>
            <div className="mood-strip-controls">
              <div className="mood-toggle">
                <button
                  className={`mood-toggle-btn ${sentimentType === 'cap8' ? 'active' : ''}`}
                  onClick={() => setSentimentType('cap8')}
                >Cap8</button>
                <button
                  className={`mood-toggle-btn ${sentimentType === 'market' ? 'active' : ''}`}
                  onClick={() => setSentimentType('market')}
                >Market</button>
              </div>
              <button className="btn btn-ghost btn-small" onClick={() => navigate('/clients')}>
                View all →
              </button>
            </div>
          </div>

          <div className="mood-chips">
            {moodChips.length === 0 && stableClients.length === 0 ? (
              <p className="text-muted text-small">No sentiment data yet</p>
            ) : (
              <>
                {moodChips.map(chip => (
                  <div
                    key={chip.client_id}
                    className={`mood-chip ${chip.trend === 'degrading' ? 'mood-chip-alert' : 'mood-chip-positive'}`}
                    onClick={() => navigate(`/clients/${chip.client_id}`)}
                  >
                    <span className="mood-chip-name">{chip.client_name}</span>
                    <div className="mood-chip-dots">
                      {chip.meetings.slice(0, 4).reverse().map((m) => {
                        const sentimentCap8 = m.sentiment_cap8;
                        const sentimentMarket = m.sentiment_market;
                        const color = sentimentType === 'cap8'
                          ? (sentimentCap8 ? SENTIMENT_CAP8_COLORS[sentimentCap8] : 'var(--text-tertiary)')
                          : (sentimentMarket ? SENTIMENT_MARKET_COLORS[sentimentMarket] : 'var(--text-tertiary)');
                        return (
                          <div
                            key={m.transcript_id}
                            className="mood-chip-dot"
                            style={{ backgroundColor: color }}
                          />
                        );
                      })}
                    </div>
                    <span className={`mood-chip-trend ${chip.trend}`}>
                      {chip.trend === 'degrading' ? '▼' : '▲'}
                    </span>
                  </div>
                ))}

                {stableClients.length > 0 && (
                  <button
                    className="mood-chip mood-chip-stable-toggle"
                    onClick={() => setShowStable(!showStable)}
                  >
                    +{stableClients.length} stable {showStable ? '▴' : '▾'}
                  </button>
                )}

                {showStable && stableClients.map(chip => (
                  <div
                    key={chip.client_id}
                    className="mood-chip mood-chip-stable"
                    onClick={() => navigate(`/clients/${chip.client_id}`)}
                  >
                    <span className="mood-chip-name">{chip.client_name}</span>
                    <div className="mood-chip-dots">
                      {chip.meetings.slice(0, 4).reverse().map((m) => {
                        const sentimentCap8 = m.sentiment_cap8;
                        const sentimentMarket = m.sentiment_market;
                        const color = sentimentType === 'cap8'
                          ? (sentimentCap8 ? SENTIMENT_CAP8_COLORS[sentimentCap8] : 'var(--text-tertiary)')
                          : (sentimentMarket ? SENTIMENT_MARKET_COLORS[sentimentMarket] : 'var(--text-tertiary)');
                        return (
                          <div
                            key={m.transcript_id}
                            className="mood-chip-dot"
                            style={{ backgroundColor: color }}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Attention cards row — max 3, non-investors only */}
        {!isInvestor && (overdueCommitment || degradingClient || triageTotal > 0) && (
          <div className="attention-cards-row">
            {/* Overdue commitment */}
            {overdueCommitment && (
              <div
                className="card-glass attention-card"
                onClick={() => navigate(`/clients/${overdueCommitment.client_id}?tab=commitments`)}
                title="View commitments"
              >
                <div className="attention-card-label">Overdue</div>
                <div className="attention-card-text">
                  {overdueCommitment.commitment_text.length > 80
                    ? overdueCommitment.commitment_text.slice(0, 80) + '…'
                    : overdueCommitment.commitment_text}
                </div>
                <div className="attention-card-meta">{overdueCommitment.client_name}</div>
              </div>
            )}

            {/* Degrading relationship */}
            {degradingClient && (
              <div
                className="card-glass attention-card attention-card-alert"
                onClick={() => navigate('/pulse')}
                title="View pulse"
              >
                <div className="attention-card-label">Relationship</div>
                <div className="attention-card-text">
                  {degradingClient.client_name} relationship degrading
                </div>
                {degradingClient.degrading_reason && (
                  <div className="attention-card-meta">
                    {degradingClient.degrading_reason.length > 80
                      ? degradingClient.degrading_reason.slice(0, 80) + '…'
                      : degradingClient.degrading_reason}
                  </div>
                )}
              </div>
            )}

            {/* Triage queue */}
            {triageTotal > 0 && triageOldest && (
              <div
                className="card-glass attention-card"
                onClick={() => navigate('/roadmap?tab=triage')}
                title="Go to triage"
              >
                <div className="attention-card-label">Triage ({triageTotal})</div>
                <div className="attention-card-text">
                  {triageOldest.insight_text.length > 80
                    ? triageOldest.insight_text.slice(0, 80) + '…'
                    : triageOldest.insight_text}
                </div>
                <div className="attention-card-meta">
                  {triageOldest.client_name}
                  {triageOldest.meeting_date ? ` · ${triageOldest.meeting_date}` : ''}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
